import React, { ComponentPropsWithoutRef, useCallback } from 'react';

import PropTypes from 'prop-types';

import { AnimatePresence } from '@adsk/alloy-react-animation';
import { variantSwitch } from '@adsk/alloy-react-helpers';
import Portal from '@adsk/alloy-react-portal';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import {
  Overlay,
  Header,
  Body,
  Footer,
  Close,
  PrevArrow,
  NextArrow,
} from './layout';

const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) =>
  e.stopPropagation();

const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  EXTRA_LARGE: 'extra_large',
} as const;

type Sizes = typeof SIZES;
export type Size = Sizes[keyof Sizes] | undefined;

type OverlayProps = ComponentPropsWithoutRef<typeof Overlay>;
type ModalProps = {
  open?: boolean;
  width?: string | number;
  height?: string | number;
  container?: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  hideOverlay?: boolean;
  onClose?: (e?: React.MouseEvent) => void;
  shouldCloseOnEsc?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  size?: Size;
} & Pick<OverlayProps, 'onAnimationStart' | 'onAnimationComplete'> & {
    overlayStyle?: OverlayProps['style'];
  };

/**
 * A modal is an overlay that focuses the customer's attention on a single task or set of controls.
 * It creates a mode that disables the main window but keeps it visible with the modal window as a child window in front of it.
 * Users must interact with the modal window before they can return to the parent application.
 */
const Modal = React.forwardRef<
  HTMLDivElement,
  ModalProps & Omit<StylableComponent<HTMLDivElement>, 'onAnimationStart'>
>(
  (
    {
      children,
      className,
      style,
      open,
      height,
      width,
      container,
      hideOverlay,
      onClose,
      onAnimationStart,
      onAnimationComplete,
      overlayStyle,
      shouldCloseOnEsc,
      shouldCloseOnOverlayClick,
      size,
      ...props
    },
    ref
  ) => {
    const onClick = useCallback(
      (e) => {
        shouldCloseOnOverlayClick && onClose?.(e);
      },
      [shouldCloseOnOverlayClick, onClose]
    );
    const onEscape = useCallback(() => {
      shouldCloseOnEsc && onClose?.();
    }, [shouldCloseOnEsc, onClose]);
    return (
      <Portal container={container}>
        <AnimatePresence>
          {open && (
            <Overlay
              data-testid="modal-overlay"
              hide={hideOverlay}
              onClick={onClick}
              onEscape={onEscape}
              onAnimationStart={onAnimationStart}
              onAnimationComplete={onAnimationComplete}
              style={overlayStyle}
            >
              <div
                {...props}
                className={className}
                ref={ref}
                onClick={stopPropagation}
                css={[
                  {
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    width:
                      width ||
                      variantSwitch({
                        statement: size,
                        variants: {
                          [SIZES.SMALL]: 420,
                          [SIZES.MEDIUM]: 600,
                          [SIZES.LARGE]: 800,
                          [SIZES.EXTRA_LARGE]: 1140,
                        },
                      }) ||
                      undefined,
                    height:
                      height ||
                      variantSwitch({
                        statement: size,
                        variants: {
                          [SIZES.SMALL]: 359,
                          [SIZES.MEDIUM]: 480,
                          [SIZES.LARGE]: 644,
                          [SIZES.EXTRA_LARGE]: 733,
                        },
                      }) ||
                      undefined,
                    outline: 'none',
                    backgroundColor: theme.colors.white,
                    borderRadius: theme.borderRadius.medium,
                    boxShadow: 'rgba(0, 0, 0, 0.25) 0px 0px 8px 0px',
                    zIndex: theme.zIndex.modal,

                    ...(hideOverlay && {
                      pointerEvents: 'all',
                    }),
                  },
                  style,
                ]}
              >
                {children}
              </div>
            </Overlay>
          )}
        </AnimatePresence>
      </Portal>
    );
  }
);

Modal.displayName = 'Modal';

Modal.defaultProps = {
  size: SIZES.MEDIUM,
  container: document.body,
  shouldCloseOnEsc: true,
  shouldCloseOnOverlayClick: true,
};

Modal.propTypes = {
  /** The content of modal */
  children: PropTypes.node,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** is modal open */
  open: PropTypes.bool,
  /** modal width, default is 400px */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** modal height, default is 400px */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Container Used for attaching the modal as a child component to the container */
  container: PropTypes.object,
  /** hide modal Overlay */
  hideOverlay: PropTypes.bool,
  /** called on click close modal */
  onClose: PropTypes.func,
  /** A callback that's called when the modal has started open animation */
  onAnimationStart: PropTypes.func,
  /** A callback that's called when the modal has Complete Animation close animation */
  onAnimationComplete: PropTypes.func,
  /** Set whether modal should be closed when pressing on esc button */
  shouldCloseOnEsc: PropTypes.bool,
  /** Set whether modal should be closed when click on overlay */
  shouldCloseOnOverlayClick: PropTypes.bool,
  /** Modal size */
  size: PropTypes.oneOf(Object.values(SIZES)),
};

export default Object.assign(Modal, {
  Overlay,
  Header,
  Body,
  Footer,
  Close,
  PrevArrow,
  NextArrow,
  SIZES,
});
